import { useDeviceDetect } from '@/utils/useMediaQuery';
import { cn } from '@/utils/utils';
import type {
  FilterTypeWithValue,
  QueryFilterOptions,
  QueryFilterOptionsWithValues,
} from '@shelflix/schemas/_common/filter.types';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card } from '@shelflix/ui-web/components/ui/card';
import { DialogTitle } from '@shelflix/ui-web/components/ui/dialog';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader } from '@shelflix/ui-web/components/ui/drawer';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useGenericFilter } from './context';
import { SearchableSelect } from './SearchableSelect';
import AppIcon from '@shelflix/ui-web/components/AppIcon';
import searchSvg from '@/assets/icons-normalized/search.svg?raw';

/**
 * A generic filter component that provides search, filter, and sort functionalities.
 *
 * @template Search - The type of the search field.
 * @template Filter - The type of the filter field, containing `name` and `condition`.
 * @template FilterWIthValue - The type of the filter field with an additional `value` property.
 * @template Sort - The type of the sort field.
 *
 * @param {Object} props - The properties for the GenericFilter component.
 * @param {QueryFilterOptions<Search, Filter, Sort>} props.options - The configuration options for search, filter, and sort.
 * @param {(params: QueryFilterOptionsWithValues<Search, FilterWIthValue, Sort>) => void} [props.onChange] - Callback triggered when the filter values change.
 */
export function GenericFilter<
  Search extends string,
  Filter extends { name: string; condition: string },
  FilterWIthValue extends { name: string; condition: string; value: unknown },
  Sort extends string,
>({
  customComponents = [],
  options,
  onChange,
  initialValue,
  initialFilters,
  isLoading = false,
}: {
  customComponents?: ReactElement[];
  options: QueryFilterOptions<Search, Filter, Sort>;
  onChange?: (params: QueryFilterOptionsWithValues<Search, FilterWIthValue, Sort>) => void;
  initialValue?: QueryFilterOptionsWithValues<Search, FilterWIthValue, Sort>['search'];
  initialFilters?: QueryFilterOptionsWithValues<Search, FilterWIthValue, Sort>['filter'];
  isLoading?: boolean;
}) {
  const [appliedValues, setAppliedValues] = useState<QueryFilterOptionsWithValues<Search, FilterWIthValue, Sort>>(
    () => ({
      filter: initialFilters,
      search: initialValue,
    })
  );
  const [tempValues, setTempValues] = useState<QueryFilterOptionsWithValues<Search, FilterWIthValue, Sort>>(() => ({
    filter: initialFilters,
    search: initialValue,
  }));
  const [searchInput, setSearchInput] = useState<string>(initialValue?.value || '');
  const hasInitialized = useRef(false);

  const { isMobile } = useDeviceDetect();

  const { openMobile, setOpenMobile, setHasFilter } = useGenericFilter();

  // Trigger onChange on mount if initialFilters is provided
  useEffect(() => {
    if (!hasInitialized.current && initialFilters && initialFilters.length > 0) {
      hasInitialized.current = true;
      onChange?.({ filter: initialFilters, search: initialValue } as QueryFilterOptionsWithValues<
        Search,
        FilterWIthValue,
        Sort
      >);
    }
  }, [initialFilters, onChange]);

  useEffect(() => {
    if (openMobile) {
      setTempValues(appliedValues);
      setSearchInput(appliedValues.search?.value ?? '');
    }
  }, [openMobile]);

  useEffect(() => {
    setHasFilter(true);
    return () => setHasFilter(false); // limpa ao desmontar
  }, [setHasFilter]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (options.search && (searchInput.length > 0 || !!tempValues.search)) {
        setTempValues((prev) => ({
          ...prev,
          search: { fields: options.search?.fields ?? [], value: searchInput },
        }));
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    if (!isMobile) {
      setAppliedValues(tempValues);
      onChange?.(tempValues);
    }
  }, [tempValues]);

  const currentValues = isMobile ? tempValues : appliedValues;

  const filterComponent = (
    <Card
      className={cn(
        'flex w-full flex-row rounded-[10px] border-none bg-[#0E0E0E] p-4 shadow-none',
        isMobile && 'flex-col'
      )}
      style={{ justifyContent: isMobile ? 'space-between' : 'start', gap: isMobile ? 12 : 16 }}
    >
      {options.search && (
        <div className="flex-1" style={{ ...(options.search.order && { order: options.search.order }) }}>
          <span className="font-regular text-xs text-[#B2A598]">{options.search.label}</span>

          <Input
            iconInside
            prependIcon={
              <AppIcon
                source="svg"
                name="search"
                svgContent={searchSvg}
                size={20}
                weight={400}
                colorStyle={{ type: 'solid', color: '#B2A598' }}
              />
            }
            onChange={(e) => setSearchInput(e.target.value)}
            value={searchInput}
            disabled={isLoading}
            className="border-[#0E0E0E] bg-[#191919] text-sm text-[#B2A598]"
          />
        </div>
      )}

      {options.filter?.length &&
        options.filter.map((option, index) => {
          const selectedValue = currentValues.filter?.find((f) => f.field.name === option.field.name)?.field.value;
          return (
            <div className="flex-1" key={'option-' + index} style={{ ...(option.order && { order: option.order }) }}>
              <span className="font-regular text-xs text-[#B2A598]">{option.label}</span>

              {(() => {
                const handleValueChange = (val: string) => {
                  const others = currentValues.filter?.filter((f) => f.field.name !== option.field.name) ?? [];
                  const next =
                    val === 'clear'
                      ? others
                      : [
                          ...others,
                          {
                            field: {
                              name: option.field.name,
                              condition: option.field.condition,
                              value: JSON.parse(val),
                            },
                          },
                        ];
                  const filter = (next.length > 0 ? next : undefined) as unknown as
                    | FilterTypeWithValue<FilterWIthValue>[]
                    | undefined;
                  setTempValues({ ...currentValues, filter });
                };
                const value = selectedValue !== undefined ? JSON.stringify(selectedValue) : 'clear';

                return option.searchable ? (
                  <SearchableSelect
                    value={value}
                    items={option.items}
                    onValueChange={handleValueChange}
                    disabled={isLoading}
                    portalled={!isMobile}
                  />
                ) : (
                  <Select value={value} onValueChange={handleValueChange} disabled={isLoading}>
                    <SelectTrigger className="h-11 w-full border border-[#0E0E0E] bg-[#191919] text-sm text-[#B2A598] [&>svg]:text-[#B2A598]! [&>svg]:opacity-100">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#191919] text-sm text-[#B2A598]">
                      <SelectItem value="clear">Todos</SelectItem>
                      {option.items.map((item) => (
                        <SelectItem key={index + '-' + item.text} value={JSON.stringify(item.value)}>
                          {item.text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              })()}
            </div>
          );
        })}

      {options.sort && (
        <div className="flex-1" style={{ ...(options.sort.order && { order: options.sort.order }) }}>
          <span className="font-regular text-xs text-[#B2A598]">{options.sort.label}</span>

          <Select
            value={
              currentValues.sort
                ? JSON.stringify({ field: currentValues.sort.field, direction: currentValues.sort.direction })
                : JSON.stringify({ field: 'clear', direction: '', text: '' })
            }
            onValueChange={(val) => {
              const parsed = JSON.parse(val);
              setTempValues({
                ...currentValues,
                sort: parsed.field === 'clear' ? undefined : { ...parsed },
              });
            }}
            disabled={isLoading}
          >
            <SelectTrigger className="h-11 w-full border border-[#0E0E0E] bg-[#191919] text-sm text-[#B2A598] [&>svg]:text-[#B2A598]! [&>svg]:opacity-100">
              <SelectValue placeholder="Selecione para ordenar..." />
            </SelectTrigger>
            <SelectContent className="bg-[#191919] text-sm text-[#B2A598]">
              <SelectItem value={JSON.stringify({ field: 'clear', direction: '', text: '' })}>
                Selecione para ordenar...
              </SelectItem>
              {options.sort.options.map((item, idx) => (
                <SelectItem
                  key={'sort-' + idx + '-' + item.text}
                  value={JSON.stringify({ field: item.field, direction: item.direction })}
                >
                  {item.text}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {customComponents.map((c) => c)}
    </Card>
  );

  return isMobile ? (
    <Drawer open={openMobile} onOpenChange={setOpenMobile}>
      <DrawerContent className=" ">
        <DrawerHeader>
          <div className="flex items-center justify-center gap-4">
            <div
              className={`relative flex h-12 w-12 items-center justify-center overflow-hidden rounded-[5px]`}
              style={{
                backgroundColor: 'var(--secondary)',
              }}
            >
              <Icon size={36} name="filter_alt" fill={1} className="text-primary" />
            </div>
            <DialogTitle className="text-primary text-[18px] font-semibold">Filtro de informações</DialogTitle>
          </div>
        </DrawerHeader>
        {filterComponent}
        <DrawerFooter>
          <Button
            onClick={() => {
              setAppliedValues(tempValues);
              onChange?.(tempValues);
              setOpenMobile(false);
            }}
            className={cn('mx-4', isMobile && 'mb-16')}
          >
            Filtrar
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  ) : (
    filterComponent
  );
}
